import { supabase } from '../supabase';

/* ============================================
   CONSTANTS
   ============================================ */

export const FREE_VOICE_LIMIT = 2;          // sessions/month
export const FREE_VIDEO_LIMIT = 1;          // sessions/month
export const MAX_TEXT_PER_CALL = 250;       // chars per question
export const MAX_GLOBAL_CHARS_PER_DAY = 5000;
export const MAX_GLOBAL_CLIPS_PER_DAY = 50;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 60_000;

/* ============================================
   UTILITIES
   ============================================ */

export function makeSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function truncateForVoice(text, maxChars = MAX_TEXT_PER_CALL) {
  const t = String(text || '').trim();
  if (t.length <= maxChars) return t;
  // Cut at last sentence/clause boundary if possible
  const slice = t.slice(0, maxChars);
  const cut = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('? '), slice.lastIndexOf('! '));
  if (cut > maxChars * 0.6) return slice.slice(0, cut + 1);
  return slice.replace(/\s+\S*$/, '').trim() + '…';
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function startOfMonthIso() {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/* ============================================
   QUOTA
   ============================================ */

// Returns { voice: {used, limit}, video: {used, limit}, isPremium,
//           globalChars, globalClips, canVoice, canVideo, blockedReason? }
export async function getVoiceQuota(userId, isPremium = false) {
  if (!userId) {
    return {
      voice: { used: 0, limit: FREE_VOICE_LIMIT },
      video: { used: 0, limit: FREE_VIDEO_LIMIT },
      isPremium: false,
      globalChars: 0, globalClips: 0,
      canVoice: false, canVideo: false,
      blockedReason: 'not_signed_in',
    };
  }

  let voiceUsed = 0;
  let videoUsed = 0;
  try {
    const { data, error } = await supabase
      .from('voice_usage')
      .select('session_id, mode')
      .eq('user_id', userId)
      .gte('created_at', startOfMonthIso());
    if (error) throw error;
    const voiceSessions = new Set();
    const videoSessions = new Set();
    for (const row of (data || [])) {
      if (row.mode === 'voice') voiceSessions.add(row.session_id);
      else if (row.mode === 'video') videoSessions.add(row.session_id);
    }
    voiceUsed = voiceSessions.size;
    videoUsed = videoSessions.size;
  } catch (_) {
    // Silent — quota check failure should not block UI; errors will surface on actual call
  }

  let globalChars = 0;
  let globalClips = 0;
  try {
    const { data, error } = await supabase.rpc('get_voice_usage_today_totals');
    if (!error) {
      const row = Array.isArray(data) ? data[0] : data;
      globalChars = Number(row?.total_chars || 0);
      globalClips = Number(row?.total_clips || 0);
    }
  } catch (_) { /* silent */ }

  const voiceLimit = isPremium ? Infinity : FREE_VOICE_LIMIT;
  const videoLimit = isPremium ? Infinity : FREE_VIDEO_LIMIT;
  const personalVoiceOk = voiceUsed < voiceLimit;
  const personalVideoOk = videoUsed < videoLimit;
  const globalCharsOk = globalChars < MAX_GLOBAL_CHARS_PER_DAY;
  const globalClipsOk = globalClips < MAX_GLOBAL_CLIPS_PER_DAY;

  let blockedReason = null;
  if (!globalCharsOk && !globalClipsOk) blockedReason = 'global_cap';
  else if (!globalCharsOk) blockedReason = 'global_voice_cap';
  else if (!globalClipsOk) blockedReason = 'global_video_cap';

  return {
    voice: { used: voiceUsed, limit: voiceLimit },
    video: { used: videoUsed, limit: videoLimit },
    isPremium,
    globalChars,
    globalClips,
    canVoice: personalVoiceOk && globalCharsOk,
    canVideo: personalVideoOk && globalClipsOk,
    blockedReason,
  };
}

/* ============================================
   USAGE LOGGING
   ============================================ */

async function logVoiceUsage({ userId, sessionId, mode, textLength, latencyMs, didClipId }) {
  if (!userId) return;
  try {
    await supabase.from('voice_usage').insert({
      user_id: userId,
      session_id: sessionId,
      mode,
      text_length: textLength || 0,
      latency_ms: latencyMs || null,
      did_clip_id: didClipId || null,
    });
  } catch (e) {
    // Logging failures should never break the user experience
    // eslint-disable-next-line no-console
    console.warn('[voiceData] usage log failed', e?.message || e);
  }
}

/* ============================================
   TTS — fetch + decode + play
   Returns: { audio (HTMLAudioElement), audioUrl, cleanup, replay }
   Throws on failure — caller should fall back gracefully.
   ============================================ */

export async function playTextAsVoice({ userId, sessionId, text }) {
  const trimmed = truncateForVoice(text);
  if (!trimmed) throw new Error('Empty text');

  const startedAt = Date.now();
  const response = await fetch('/.netlify/functions/voice-tts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: trimmed }),
  });

  let payload;
  try { payload = await response.json(); }
  catch (_) { throw new Error('Voice service returned invalid response'); }

  if (!response.ok) {
    throw new Error(payload?.error || `Voice failed (${response.status})`);
  }
  if (!payload.audio_base64) {
    throw new Error('Voice service returned no audio');
  }

  // Decode base64 → Blob → object URL
  const bin = atob(payload.audio_base64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: payload.mime || 'audio/mpeg' });
  const audioUrl = URL.createObjectURL(blob);

  const audio = new Audio(audioUrl);
  audio.preload = 'auto';

  // Log usage (non-blocking)
  logVoiceUsage({
    userId,
    sessionId,
    mode: 'voice',
    textLength: payload.text_length || trimmed.length,
    latencyMs: payload.latency_ms || (Date.now() - startedAt),
  });

  // Auto-play (browser may block if not user-initiated; caller handles)
  let playPromise;
  try { playPromise = audio.play(); } catch (_) { /* swallow, user can replay */ }
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(() => { /* autoplay blocked — listener can click replay */ });
  }

  const cleanup = () => {
    try { audio.pause(); } catch (_) {}
    try { URL.revokeObjectURL(audioUrl); } catch (_) {}
  };
  const replay = () => {
    try { audio.currentTime = 0; audio.play().catch(() => {}); } catch (_) {}
  };

  return { audio, audioUrl, cleanup, replay };
}

/* ============================================
   D-ID — create + poll + return video URL
   ============================================ */

export async function generateAvatarVideo({ userId, sessionId, text, onProgress, signal }) {
  const trimmed = truncateForVoice(text);
  if (!trimmed) throw new Error('Empty text');

  const startedAt = Date.now();

  // 1. Create the clip
  const createRes = await fetch('/.netlify/functions/did-clip-create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: trimmed }),
    signal,
  });
  let createPayload;
  try { createPayload = await createRes.json(); }
  catch (_) { throw new Error('Avatar service returned invalid response'); }
  if (!createRes.ok || !createPayload.clip_id) {
    throw new Error(createPayload?.error || `Avatar create failed (${createRes.status})`);
  }
  const clipId = createPayload.clip_id;
  if (onProgress) onProgress({ phase: 'created', elapsedMs: Date.now() - startedAt });

  // 2. Poll until done
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let lastStatus = createPayload.status || 'created';
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new Error('Cancelled');
    await sleep(POLL_INTERVAL_MS);

    const statusRes = await fetch('/.netlify/functions/did-clip-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clip_id: clipId }),
      signal,
    });
    let statusPayload;
    try { statusPayload = await statusRes.json(); }
    catch (_) { throw new Error('Avatar status returned invalid response'); }
    if (!statusRes.ok) {
      throw new Error(statusPayload?.error || `Status check failed (${statusRes.status})`);
    }

    lastStatus = statusPayload.status;
    const elapsedMs = Date.now() - startedAt;
    if (onProgress) onProgress({ phase: lastStatus, elapsedMs });

    if (lastStatus === 'done' && statusPayload.result_url) {
      // Log usage
      logVoiceUsage({
        userId,
        sessionId,
        mode: 'video',
        textLength: trimmed.length,
        latencyMs: elapsedMs,
        didClipId: clipId,
      });
      return { videoUrl: statusPayload.result_url, clipId, latencyMs: elapsedMs };
    }
    if (lastStatus === 'error' || lastStatus === 'rejected') {
      throw new Error('Avatar generation failed: ' + (statusPayload.error || lastStatus));
    }
  }
  throw new Error(`Avatar generation timed out after ${Math.round(POLL_TIMEOUT_MS / 1000)}s (last status: ${lastStatus})`);
}
